import React, { useState, useContext, useMemo, useEffect } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import { Button, Row, Col, Table, Input, Label, ButtonGroup } from "reactstrap";
import axios from "axios";
import ExportJsonExcel from "js-export-excel";
import moment from "moment";

// Corte diario: en una sola pantalla todos los movimientos del periodo
// (entradas, salidas, devoluciones, ajustes), el sobrante (existencia actual) y
// un resumen por variante que los cruza. Patron tomado de TextilesBasicos.
const TABS = [
  { key: "resumen", label: "Resumen", titulo: "RESUMEN POR MODELO, COLOR Y TALLA" },
  { key: "entradas", label: "Entradas", titulo: "ENTRADAS (COMPRAS)", folio: "Compra", tercero: "Proveedor" },
  { key: "salidas", label: "Salidas", titulo: "SALIDAS (SURTIDOS)", folio: "Surtido", tercero: "Cliente" },
  { key: "devClientes", label: "Dev. Clientes", titulo: "DEVOLUCIONES DE CLIENTES", folio: "Devolucion", tercero: "Cliente" },
  { key: "devProveedores", label: "Dev. Proveedores", titulo: "DEVOLUCIONES A PROVEEDORES", folio: "Devolucion", tercero: "Proveedor" },
  { key: "ajustes", label: "Ajustes", titulo: "AJUSTES DE INVENTARIO", folio: "Ajuste", tercero: "Observaciones" },
  { key: "sobrante", label: "Sobrante", titulo: "SOBRANTE (EXISTENCIA ACTUAL)" },
];

const num = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(Number(n) || 0);
const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n) || 0);

function CorteDiario() {
  const { user } = useContext(AuthContext);
  const URL_CORTE_DIARIO = process.env.REACT_APP_URL_CORTE_DIARIO;
  const hoy = moment(new Date()).format("YYYY-MM-DD");

  const [fechaInicio, setFechaInicio] = useState(hoy);
  const [fechaFin, setFechaFin] = useState(hoy);
  const [datos, setDatos] = useState({});
  const [cargando, setCargando] = useState(false);
  const [tab, setTab] = useState("resumen");
  const [modeloFiltro, setModeloFiltro] = useState("");

  useEffect(() => {
    if (!fechaInicio || !fechaFin) return;
    setCargando(true);
    axios
      .get(`${URL_CORTE_DIARIO}/${fechaInicio}/${fechaFin}`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((r) => { setDatos(r.data || {}); setCargando(false); })
      .catch((err) => { console.log(err); setCargando(false); });
  }, [fechaInicio, fechaFin]); // eslint-disable-line

  const lista = (k) => datos[k] || [];
  const filtra = (arr) => (modeloFiltro ? arr.filter((a) => a.modelo === modeloFiltro) : arr);

  // Modelos presentes en cualquier movimiento, para el selector de filtro
  const modelos = useMemo(() => {
    const s = new Set();
    ["dataEntradas", "dataSalidas", "dataDevClientes", "dataDevProveedores", "dataAjustes", "dataSobrante"]
      .forEach((k) => lista(k).forEach((a) => a.modelo && s.add(a.modelo)));
    return Array.from(s).sort();
  }, [datos]); // eslint-disable-line

  const porTab = {
    resumen: filtra(lista("dataResumen")),
    entradas: filtra(lista("dataEntradas")),
    salidas: filtra(lista("dataSalidas")),
    devClientes: filtra(lista("dataDevClientes")),
    devProveedores: filtra(lista("dataDevProveedores")),
    ajustes: filtra(lista("dataAjustes")),
    sobrante: filtra(lista("dataSobrante")),
  };

  const suma = (arr, campo) => arr.reduce((s, a) => s + (Number(a[campo]) || 0), 0);

  function excel() {
    const mov = (arr, cfg) =>
      arr.map((a) => ({
        Fecha: a.fecha, [cfg.folio]: a.folio, [cfg.tercero]: a.tercero, Almacen: a.almacen,
        Modelo: a.modelo, Color: a.color, Talla: a.talla, Cantidad: a.cantidad, Precio: a.precio, Total: a.total,
      }));
    const hoja = (name, data) => ({
      sheetName: name, sheetData: data,
      sheetFilter: data[0] ? Object.keys(data[0]) : [],
      sheetHeader: data[0] ? Object.keys(data[0]) : [],
    });
    const cfg = (k) => TABS.find((t) => t.key === k);
    const datas = [
      hoja("Resumen", porTab.resumen.map((a) => ({
        Modelo: a.modelo, Color: a.color, Talla: a.talla, Entradas: a.entradas, Salidas: a.salidas,
        DevClientes: a.devClientes, DevProveedores: a.devProveedores, Ajustes: a.ajustes, Neto: a.neto, Sobrante: a.sobrante,
      }))),
      hoja("Entradas", mov(porTab.entradas, cfg("entradas"))),
      hoja("Salidas", mov(porTab.salidas, cfg("salidas"))),
      hoja("DevClientes", mov(porTab.devClientes, cfg("devClientes"))),
      hoja("DevProveedores", mov(porTab.devProveedores, cfg("devProveedores"))),
      hoja("Ajustes", porTab.ajustes.map((a) => ({
        Fecha: a.fecha, Ajuste: a.folio, Observaciones: a.tercero, Autorizado: a.autorizado, Almacen: a.almacen,
        Modelo: a.modelo, Color: a.color, Talla: a.talla, Cantidad: a.cantidad,
      }))),
      hoja("Sobrante", porTab.sobrante.map((a) => ({
        Almacen: a.almacen, Modelo: a.modelo, Color: a.color, Talla: a.talla, Existencia: a.cantidad, Costo: a.precio, Total: a.total,
      }))),
    ].filter((h) => h.sheetData.length);
    new ExportJsonExcel({ fileName: `CorteDiario_${fechaInicio}_${fechaFin}`, datas }).saveExcel();
  }

  const cfgActivo = TABS.find((t) => t.key === tab);
  const filas = porTab[tab];

  const tablaMovimientos = () => (
    <Table size="sm" striped bordered responsive>
      <thead>
        <tr>
          <th>Fecha</th><th>{cfgActivo.folio}</th><th>{cfgActivo.tercero}</th>
          {tab === "ajustes" ? <th>Autorizado</th> : null}
          <th>Almacen</th><th>Modelo</th><th>Color</th><th>Talla</th>
          <th className="text-right">Cantidad</th>
          {tab !== "ajustes" ? <><th className="text-right">Precio</th><th className="text-right">Total</th></> : null}
        </tr>
      </thead>
      <tbody>
        {filas.map((a, i) => (
          <tr key={i}>
            <td>{a.fecha}</td><td>{a.folio}</td><td>{a.tercero}</td>
            {tab === "ajustes" ? <td>{a.autorizado}</td> : null}
            <td>{a.almacen}</td><td>{a.modelo}</td><td>{a.color}</td><td>{a.talla}</td>
            <td className="text-right">{num(a.cantidad)}</td>
            {tab !== "ajustes" ? <><td className="text-right">{money(a.precio)}</td><td className="text-right">{money(a.total)}</td></> : null}
          </tr>
        ))}
        <tr className="negrita">
          <td colSpan={tab === "ajustes" ? 8 : 7} className="text-right">TOTAL</td>
          <td className="text-right">{num(suma(filas, "cantidad"))}</td>
          {tab !== "ajustes" ? <><td></td><td className="text-right">{money(suma(filas, "total"))}</td></> : null}
        </tr>
      </tbody>
    </Table>
  );

  const tablaSobrante = () => (
    <Table size="sm" striped bordered responsive>
      <thead>
        <tr>
          <th>Almacen</th><th>Modelo</th><th>Color</th><th>Talla</th>
          <th className="text-right">Existencia</th><th className="text-right">Costo</th><th className="text-right">Total</th>
        </tr>
      </thead>
      <tbody>
        {filas.map((a, i) => (
          <tr key={i}>
            <td>{a.almacen}</td><td>{a.modelo}</td><td>{a.color}</td><td>{a.talla}</td>
            <td className="text-right">{num(a.cantidad)}</td>
            <td className="text-right">{money(a.precio)}</td>
            <td className="text-right">{money(a.total)}</td>
          </tr>
        ))}
        <tr className="negrita">
          <td colSpan={4} className="text-right">TOTAL</td>
          <td className="text-right">{num(suma(filas, "cantidad"))}</td>
          <td></td>
          <td className="text-right">{money(suma(filas, "total"))}</td>
        </tr>
      </tbody>
    </Table>
  );

  // Resumen: subtotal por modelo al cambiar de modelo, como en TextilesBasicos
  const tablaResumen = () => {
    const rows = [];
    const campos = ["entradas", "salidas", "devClientes", "devProveedores", "ajustes", "neto", "sobrante"];
    let sub = {};
    const cero = () => campos.reduce((o, c) => ({ ...o, [c]: 0 }), {});
    sub = cero();
    filas.forEach((a, i) => {
      rows.push(
        <tr key={`r${i}`}>
          <td>{a.modelo}</td><td>{a.color}</td><td>{a.talla}</td>
          {campos.map((c) => <td key={c} className="text-right">{num(a[c])}</td>)}
        </tr>
      );
      campos.forEach((c) => { sub[c] += Number(a[c]) || 0; });
      const sig = filas[i + 1];
      if (!sig || sig.modelo !== a.modelo) {
        rows.push(
          <tr key={`s${i}`} className="negrita" style={{ background: "#f1f5f9" }}>
            <td>{a.modelo}</td><td colSpan={2} className="text-right">Subtotal</td>
            {campos.map((c) => <td key={c} className="text-right">{num(sub[c])}</td>)}
          </tr>
        );
        sub = cero();
      }
    });
    return (
      <Table size="sm" bordered responsive>
        <thead>
          <tr>
            <th>Modelo</th><th>Color</th><th>Talla</th>
            <th className="text-right">Entradas</th><th className="text-right">Salidas</th>
            <th className="text-right">Dev. Cli.</th><th className="text-right">Dev. Prov.</th>
            <th className="text-right">Ajustes</th><th className="text-right">Neto periodo</th>
            <th className="text-right">Sobrante</th>
          </tr>
        </thead>
        <tbody>
          {rows}
          <tr className="negrita">
            <td colSpan={3} className="text-right">TOTAL</td>
            {campos.map((c) => <td key={c} className="text-right">{num(suma(filas, c))}</td>)}
          </tr>
        </tbody>
      </Table>
    );
  };

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_inventarios ? (
        <div className="card container col-12">
          <Row className="mb-2">
            <Col md={3}>
              <Button href="/MenuInventarios" className="btn btn-danger" size="sm">Regresar</Button>{" "}
              <Button className="btn btn-primary" size="sm" onClick={excel}>Excel</Button>
            </Col>
            <Col md={2}>
              <Label>Fecha inicio</Label>
              <Input bsSize="sm" type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
            </Col>
            <Col md={2}>
              <Label>Fecha fin</Label>
              <Input bsSize="sm" type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
            </Col>
            <Col md={3}>
              <Label>Modelo</Label>
              <Input bsSize="sm" type="select" value={modeloFiltro} onChange={(e) => setModeloFiltro(e.target.value)}>
                <option value="">Todos los modelos</option>
                {modelos.map((m) => <option key={m} value={m}>{m}</option>)}
              </Input>
            </Col>
          </Row>

          <h3 align="center">Corte Diario</h3>
          <p align="center" style={{ color: "#64748b", margin: 0 }}>
            Entradas, salidas, devoluciones y ajustes del {fechaInicio} al {fechaFin}; el sobrante es la existencia de hoy.
          </p>
          <br />
          <ButtonGroup className="flex-wrap">
            {TABS.map((t) => (
              <Button key={t.key} size="sm" color={tab === t.key ? "primary" : "light"} onClick={() => setTab(t.key)}>
                {t.label} ({porTab[t.key].length})
              </Button>
            ))}
          </ButtonGroup>
          <br />
          <br />
          <h5 className="negrita">{cfgActivo.titulo}</h5>
          {cargando ? (
            <h6 align="center">Cargando...</h6>
          ) : tab === "resumen" ? tablaResumen() : tab === "sobrante" ? tablaSobrante() : tablaMovimientos()}
          <br />
        </div>
      ) : undefined}
      <br />
    </>
  );
}

export default CorteDiario;
